// src/features/inventory/pages/ItemDetailPage/tabs/Hazardous/EditMSDSDialog.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  LinearProgress,
} from '@mui/material';
import { PictureAsPdf as PdfIcon, Upload as UploadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { da } from 'date-fns/locale';
import { addDays, format } from 'date-fns';
import SelectFileDialog from '../../../../../files/components/SelectFileDialog/SelectFileDialog';
import { useFiles } from '../../../../../files/hooks/useFiles';
import { invalidateFileUrl } from '../../../../../files/hooks/useFileUrl';
import { FileDropZone, acceptsFile, useFilePicker, useUploadQueue, UploadProgressList } from '../../../../../files/upload';
import { HAZARD_LEVELS, HazardLevelLabel } from '../../../../components/HazardDot';

const PDF = 'application/pdf,.pdf';

const LANGUAGES = [
  { value: 'da', label: 'Dansk' },
  { value: 'en', label: 'English' },
];

export default function EditMSDSDialog({ open, onClose, onSave, existing, loading, startingFolderId, initialBreadcrumbs, uploadToItemFolder }) {
  const { t } = useTranslation(['inventory', 'common', 'files']);
  const isEdit = Boolean(existing);

  const [issuedDate, setIssuedDate] = useState(null);
  const [reviewDate, setReviewDate] = useState(null);
  const [language, setLanguage] = useState('da');
  const [hazardLevel, setHazardLevel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [eyeContact, setEyeContact] = useState('');
  const [disposal, setDisposal] = useState('');
  const [ppe, setPpe] = useState('');
  const [storage, setStorage] = useState('');
  const [spillHandling, setSpillHandling] = useState('');
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  // A replacement PDF for the current file. Uploaded as a new version of it on
  // save (not before, so cancelling leaves no stray version behind).
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const versionInputRef = useRef(null);
  const { uploadFileVersion, deleteFile } = useFiles();
  // A PDF uploaded in this dialog only belongs to the sheet once it's saved, so
  // it's deleted again if the dialog is cancelled or another file replaces it
  const uploadedHere = useRef(null);
  const discardUploaded = () => {
    if (uploadedHere.current) deleteFile(uploadedHere.current);
    uploadedHere.current = null;
  };

  useEffect(() => {
    if (open) {
      const today = new Date();
      setIssuedDate(today);
      setReviewDate(addDays(today, 365));
      if (existing) {
        setLanguage(existing.language || 'da');
        setHazardLevel(existing.hazard_level || '');
        setSelectedFile(existing.file ? { file_id: existing.file.file_id, filename: existing.file.filename } : null);
        setEyeContact(existing.eye_contact || '');
        setDisposal(existing.disposal || '');
        setPpe(existing.ppe || '');
        setStorage(existing.storage || '');
        setSpillHandling(existing.spill_handling || '');
      } else {
        setLanguage('da');
        setHazardLevel('');
        setSelectedFile(null);
        setEyeContact('');
        setDisposal('');
        setPpe('');
        setStorage('');
        setSpillHandling('');
      }
      setError(null);
      setNewVersionFile(null);
      setUploadProgress(null);
      uploadedHere.current = null;
    }
  }, [open, existing]);

  const handleIssuedDateChange = (date) => {
    setIssuedDate(date);
    if (date) {
      setReviewDate(addDays(date, 365));
    }
  };

  const handleSelectFile = (file) => {
    if (file?.file_id !== uploadedHere.current) discardUploaded();
    setSelectedFile(file ? { file_id: file.file_id, filename: file.filename } : null);
    setNewVersionFile(null);
    setFileDialogOpen(false);
  };

  const handleVersionPicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setNewVersionFile(file);
  };

  // Only the file already linked can get a new version; a file picked with
  // "Skift fil" is a different file.
  const canUploadVersion = isEdit && selectedFile && selectedFile.file_id === existing.file?.file_id;

  // A PDF from the computer when no file is linked yet: uploaded to the item's
  // folder straight away and linked on save
  const newFileUploads = useUploadQueue({
    accept: PDF,
    upload: (file, options) => uploadToItemFolder(file, options),
    onUploaded: (result, file) => {
      discardUploaded();
      uploadedHere.current = result.data.data.file_id;
      setSelectedFile({ file_id: result.data.data.file_id, filename: file.name });
      setNewVersionFile(null);
    },
  });
  const newFilePicker = useFilePicker({ onFiles: (files) => newFileUploads.enqueue(files.slice(0, 1)), accept: PDF, multiple: false });

  // Dropped PDF: a new version of the linked file, or the first file for the sheet
  const handleDroppedPdf = ([file]) => {
    if (!acceptsFile(file, PDF)) {
      setError(t('files:upload.wrong_type'));
      return;
    }
    setError(null);
    if (canUploadVersion) setNewVersionFile(file);
    else newFileUploads.enqueue([file]);
  };

  const uploading = uploadProgress !== null || newFileUploads.busy;
  const busy = loading || uploading;

  const handleCancel = () => {
    discardUploaded();
    onClose();
  };

  const handleSave = async () => {
    if (!issuedDate || !reviewDate) {
      setError(t('common:validation.required_fields'));
      return;
    }
    setError(null);
    if (newVersionFile) {
      setUploadProgress(0);
      const result = await uploadFileVersion(
        selectedFile.file_id,
        newVersionFile,
        t('inventory:msds.dialog.version_change_note', { version: existing.version + 1 }),
        { onUploadProgress: (ev) => ev.total && setUploadProgress(Math.round((ev.loaded / ev.total) * 100)) },
      );
      setUploadProgress(null);
      if (!result?.success) {
        setError(result?.error || t('inventory:msds.tab.save_error'));
        return;
      }
      invalidateFileUrl(selectedFile.file_id);
      setNewVersionFile(null);
    }
    await onSave({
      issued_date: format(issuedDate, 'yyyy-MM-dd'),
      review_date: format(reviewDate, 'yyyy-MM-dd'),
      language,
      hazard_level: hazardLevel || null,
      file_id: selectedFile?.file_id || null,
      eye_contact: eyeContact || null,
      disposal: disposal || null,
      ppe: ppe || null,
      storage: storage || null,
      spill_handling: spillHandling || null,
    });
  };

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEdit ? t('inventory:msds.dialog.title_edit') : t('inventory:msds.dialog.title_create')}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Row 1: dates + language */}
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <DatePicker
                  label={t('inventory:msds.dialog.issued_date')}
                  value={issuedDate}
                  onChange={handleIssuedDateChange}
                  slotProps={{ textField: { size: 'small', sx: { flex: 1, minWidth: 160 } } }}
                />
                <DatePicker
                  label={t('inventory:msds.dialog.review_date')}
                  value={reviewDate}
                  onChange={setReviewDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      helperText: t('inventory:msds.dialog.review_date_helper'),
                      sx: { flex: 1, minWidth: 160 },
                    },
                  }}
                />
                <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                  <InputLabel id="edit-m-s-d-s-dialog-language-label">{t('inventory:msds.dialog.language')}</InputLabel>
                  <Select labelId="edit-m-s-d-s-dialog-language-label" value={language} label={t('inventory:msds.dialog.language')} onChange={(e) => setLanguage(e.target.value)}>
                    {LANGUAGES.map((l) => (
                      <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
                  <InputLabel id="edit-m-s-d-s-dialog-hazard-level-label">{t('inventory:msds.hazard_level.label')}</InputLabel>
                  <Select
                    labelId="edit-m-s-d-s-dialog-hazard-level-label"
                    value={hazardLevel}
                    label={t('inventory:msds.hazard_level.label')}
                    onChange={(e) => setHazardLevel(e.target.value)}
                  >
                    <MenuItem value="">
                      <Typography variant="body2" color="text.secondary">{t('inventory:msds.hazard_level.none')}</Typography>
                    </MenuItem>
                    {HAZARD_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        <HazardLevelLabel level={level} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t('inventory:msds.hazard_level.helper')}</FormHelperText>
                </FormControl>
              </Box>

              {/* Row 2: PDF; a PDF can be dropped here too */}
              <FileDropZone
                onFiles={handleDroppedPdf}
                multiple={false}
                disabled={uploading || !uploadToItemFolder}
                label={canUploadVersion ? t('inventory:msds.dialog.drop_version') : t('inventory:msds.dialog.drop_pdf')}
                sx={{ borderRadius: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ minWidth: 'fit-content' }}>
                    {t('inventory:msds.dialog.pdf_label')}
                  </Typography>
                  {selectedFile ? (
                    <Chip
                      icon={<PdfIcon />}
                      label={selectedFile.filename}
                      onDelete={uploading ? undefined : () => { discardUploaded(); setSelectedFile(null); setNewVersionFile(null); }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t('inventory:msds.dialog.no_file')}</Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    {canUploadVersion && (
                      <Button size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading} onClick={() => versionInputRef.current?.click()}>
                        {t('inventory:msds.dialog.upload_version')}
                      </Button>
                    )}
                    {!selectedFile && uploadToItemFolder && (
                      <Button size="small" variant="contained" startIcon={<UploadIcon />} disabled={uploading} onClick={newFilePicker.open}>
                        {t('inventory:msds.dialog.upload_pdf')}
                      </Button>
                    )}
                    <Button size="small" variant="outlined" disabled={uploading} onClick={() => setFileDialogOpen(true)}>
                      {selectedFile ? t('inventory:msds.dialog.change_file') : t('inventory:msds.dialog.select_file')}
                    </Button>
                  </Box>
                  <input ref={versionInputRef} type="file" accept={PDF} hidden onChange={handleVersionPicked} />
                  {newFilePicker.input}
                </Box>
                <UploadProgressList items={newFileUploads.items} onDismiss={newFileUploads.dismiss} sx={{ mt: 1.5 }} />
                {newVersionFile && (
                  <Alert
                    severity="info"
                    icon={<UploadIcon />}
                    sx={{ mt: 1.5 }}
                    onClose={uploading ? undefined : () => setNewVersionFile(null)}
                  >
                    {t('inventory:msds.dialog.version_pending', { filename: newVersionFile.name })}
                    {uploadProgress !== null && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />}
                  </Alert>
                )}
                {!selectedFile && !newVersionFile && uploadToItemFolder && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {t('inventory:msds.dialog.drop_hint')}
                  </Typography>
                )}
              </FileDropZone>

              {/* Row 3: eye contact + disposal */}
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <TextField
                  label={t('inventory:msds.fields.eye_contact')}
                  value={eyeContact}
                  onChange={(e) => setEyeContact(e.target.value)}
                  multiline
                  minRows={3}
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label={t('inventory:msds.fields.disposal')}
                  value={disposal}
                  onChange={(e) => setDisposal(e.target.value)}
                  multiline
                  minRows={3}
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>

              {/* Row 4: PPE + storage */}
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <TextField
                  label={t('inventory:msds.fields.ppe')}
                  value={ppe}
                  onChange={(e) => setPpe(e.target.value)}
                  multiline
                  minRows={3}
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label={t('inventory:msds.fields.storage')}
                  value={storage}
                  onChange={(e) => setStorage(e.target.value)}
                  multiline
                  minRows={3}
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>

              {/* Row 5: spill handling (full width) */}
              <TextField
                label={t('inventory:msds.fields.spill_handling')}
                value={spillHandling}
                onChange={(e) => setSpillHandling(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 2000 } }}
              />

            </Box>
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={busy}>{t('inventory:msds.dialog.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={busy}>
            {isEdit ? t('inventory:msds.dialog.save_revision') : t('inventory:msds.dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <SelectFileDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        onSelectFile={handleSelectFile}
        allowUpload
        accept={PDF}
        startingFolderId={startingFolderId}
        initialBreadcrumbs={initialBreadcrumbs}
      />
    </>
  );
}
